import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OwnerPicker } from "@/components/OwnerPicker";
import { useAuth } from "@/lib/auth";
import { useMvaMutations } from "@/hooks/useMvas";
import { MVA_OPEN_STATUSES, type MvaStatus } from "@shared/mvaTracker";

/** The open MVA a prospect row carries on GET /api/prospects. */
export interface ProspectMva { id: string; status: string; assignedTo: string | null; assigneeName: string | null; dueOn: string | null }

export interface MvaCandidate { id: string; name: string; industry: string | null; mva: ProspectMva | null }

/** Start an MVA: pick the prospect, who is doing it, and optionally a due date. */
export function StartMvaDialog({ open, onOpenChange, prospects, initialCompanyId }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prospects: MvaCandidate[];
  initialCompanyId?: string | null;
}) {
  const { user } = useAuth();
  const { start } = useMvaMutations();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [status, setStatus] = useState<MvaStatus>("Not started");
  const [dueOn, setDueOn] = useState("");
  const [notes, setNotes] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setCompanyId(initialCompanyId ?? null);
    setAssignedTo(user?.id ?? null);
    setStatus("Not started");
    setDueOn("");
    setNotes("");
    setQuery("");
  }, [open, initialCompanyId, user?.id]);

  const sorted = useMemo(() => [...prospects].sort((a, b) => a.name.localeCompare(b.name)), [prospects]);
  // Plain substring match. cmdk's built-in fuzzy scoring matched "Steven"
  // against scattered letters in unrelated names and buried the real hit.
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((p) => p.name.toLowerCase().includes(q) || (p.industry ?? "").toLowerCase().includes(q));
  }, [sorted, query]);
  const chosen = prospects.find((p) => p.id === companyId) ?? null;

  const submit = () => {
    if (!companyId) return;
    start.mutate(
      { companyId, assignedTo, status, dueOn: dueOn || null, notes: notes.trim() || null },
      {
        onSuccess: (row) => {
          toast.success(`MVA started for ${row.companyName}`, { description: row.assigneeName ? `Assigned to ${row.assigneeName}` : "Unassigned for now" });
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Start an MVA</DialogTitle></DialogHeader>
        {/* min-w-0: DialogContent is a grid, and a long prospect name would otherwise widen the track past the dialog. */}
        <div className="space-y-4 min-w-0">
          <div className="space-y-1.5">
            <Label>Prospect</Label>
            {chosen ? (
              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{chosen.name}</div>
                  {chosen.industry && <div className="text-xs text-muted-foreground truncate">{chosen.industry}</div>}
                </div>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setCompanyId(null)}>Change</Button>
              </div>
            ) : (
              <Command className="rounded-md border" shouldFilter={false}>
                <CommandInput placeholder="Search prospects…" autoFocus value={query} onValueChange={setQuery} />
                <CommandList className="max-h-[220px]">
                  <CommandEmpty>No prospect matches.</CommandEmpty>
                  {matches.map((p) => (
                    <CommandItem key={p.id} value={p.id} disabled={!!p.mva} onSelect={() => setCompanyId(p.id)}>
                      <div className="flex w-full min-w-0 items-center justify-between gap-2">
                        <span className="truncate min-w-0">{p.name}</span>
                        {p.mva && <span className="shrink-0 text-[11px] text-muted-foreground">MVA in progress · {p.mva.assigneeName ?? "Unassigned"}</span>}
                      </div>
                    </CommandItem>
                  ))}
                </CommandList>
              </Command>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Assigned to</Label>
              <OwnerPicker value={assignedTo} onChange={setAssignedTo} className="h-9 text-sm w-full" />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as MvaStatus)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{MVA_OPEN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Due <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} className="h-9 w-[180px]" />
          </div>

          <div className="space-y-1.5">
            <Label>Notes <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="e.g. owner sending 3 years of P&Ls this week" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!companyId || start.isPending} onClick={submit}>{start.isPending ? "Starting…" : "Start MVA"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
